import Image from "next/image";
import { ArrowRight } from "lucide-react";
import type { CSSProperties } from "react";

import {
  CcPhotoScene,
  CcToolLogo,
} from "@/components/services/shared/CcPhotoScene";
import { Mark } from "@/components/services/shared/demo-parts";

import type { LeadGenDemoCopy } from "./lead-gen-demo-copy";

/**
 * The three "Värdet av leadgenerering" tabs as real photos with cards for the
 * made-up B2B consultancy laid over them: the right accounts, personal
 * sequences, booked meetings. Copy comes from `lead-gen-demo.json`.
 */

const delay = (d: number) => ({ "--d": d }) as CSSProperties;

// ---------------------------------------------------------------- 1. the right accounts

export function LeadAccountsPhotoView({
  copy,
}: {
  copy: LeadGenDemoCopy["accounts"];
}) {
  return (
    <CcPhotoScene photo={copy.photo} variant="lead-accounts">
      <div className="cc-wv-card cc-lgv-accounts" style={delay(0)}>
        <p className="cc-wv-card__head">
          {copy.listTitle}
          <em>{copy.listMeta}</em>
        </p>
        <p className="cc-lgv-kpi">
          <b>{copy.listValue}</b>
          <span>{copy.listPeriod}</span>
        </p>
        <ul className="cc-lgv-rows">
          {copy.table.slice(0, 4).map((row) => (
            <li key={row.label}>
              <Mark logo={row.logo} badge={row.badge} />
              <span>
                {row.label}
                <small>{row.signal}</small>
              </span>
              <b>{row.score}</b>
            </li>
          ))}
        </ul>
      </div>

      <div className="cc-wv-card cc-lgv-icp" style={delay(1)}>
        <p className="cc-wv-card__head">
          {copy.icpTitle}
          <em>{copy.icpMeta}</em>
        </p>
        <ul>
          {copy.icp.map((item) => (
            <li key={item.name}>
              <span>{item.name}</span>
              <em data-tone={item.tone}>{item.status}</em>
            </li>
          ))}
        </ul>
      </div>
    </CcPhotoScene>
  );
}

// ---------------------------------------------------------------- 2. personal sequences

export function LeadSequencesPhotoView({
  copy,
}: {
  copy: LeadGenDemoCopy["sequences"];
}) {
  return (
    <CcPhotoScene photo={copy.photo} variant="lead-sequences">
      <div className="cc-wv-card cc-lgv-sequence" style={delay(0)}>
        <p className="cc-wv-card__head">
          {copy.seqTitle}
          <em>{copy.seqMeta}</em>
        </p>
        <ol>
          {copy.steps.map((step) => (
            <li key={step.label}>
              <Mark logo={step.logo} badge={step.badge} />
              <span>
                {step.label}
                <small>
                  {copy.columns.day} {step.day}
                </small>
              </span>
              <b>{step.replies}</b>
            </li>
          ))}
        </ol>
      </div>

      <div className="cc-wv-card cc-lgv-rate" style={delay(1)}>
        <p className="cc-wv-card__head">{copy.rateTitle}</p>
        <div className="cc-lgv-rate__row">
          <span>
            <small>{copy.rateBeforeLabel}</small>
            <b>{copy.rateBefore}</b>
          </span>
          <ArrowRight size={16} strokeWidth={2} />
          <span data-strong="true">
            <small>{copy.rateAfterLabel}</small>
            <b>{copy.rateAfter}</b>
          </span>
        </div>
      </div>
    </CcPhotoScene>
  );
}

// ---------------------------------------------------------------- 3. booked meetings

export function LeadMeetingsPhotoView({
  copy,
}: {
  copy: LeadGenDemoCopy["meetings"];
}) {
  const max = Math.max(...copy.meetingsSeries);
  return (
    <CcPhotoScene photo={copy.photo} variant="lead-meetings">
      <div className="cc-wv-card cc-lgv-meetings" style={delay(0)}>
        <p className="cc-wv-card__head">
          <CcToolLogo name="google-calendar" />
          {copy.inboxTitle}
          <em>{copy.inboxMeta}</em>
        </p>
        <ul>
          {copy.rows.slice(0, 3).map((row) => (
            <li key={row.name}>
              {row.photo ? (
                <Image src={row.photo} alt="" width={30} height={30} />
              ) : (
                <i>{row.initials}</i>
              )}
              <span>
                {row.name}
                <small>{row.subject}</small>
              </span>
              <em data-tone={row.tone}>{row.status}</em>
            </li>
          ))}
        </ul>
      </div>

      <div className="cc-wv-card cc-lgv-count" style={delay(1)}>
        <p className="cc-wv-card__head">{copy.meetingsTitle}</p>
        <p className="cc-lgv-kpi">
          <b>{copy.meetingsValue}</b>
          <span>{copy.meetingsPeriod}</span>
        </p>
        <ul className="cc-sv-visits__bars">
          {copy.meetingsSeries.map((value, index) => (
            <li
              key={index}
              style={
                {
                  "--h": `${(value / max) * 100}%`,
                  "--i": index,
                } as CSSProperties
              }
            />
          ))}
        </ul>
        <small className="cc-lgv-kpi__change">{copy.meetingsChange}</small>
      </div>
    </CcPhotoScene>
  );
}
